// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xIsPickup } from "@keller/App/Store/Selectors/GeneralSelectors";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import { color, xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    isReordering: state.pages.orderHistory.isReordering,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
});

const mapDispatchToProps = {
    reorder,
};

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasToasterContext;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    companyNameCell?: DataTableCellProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    reorderButton?: ButtonPresentationProps;
    customerPOCell?: DataTableCellProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
    reorderButton: {
        sizeVariant: "small",
        variant: "secondary",
        mergeCss: true,
        css: css`
            width: 100%;
        `,
    },
    companyNameCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    customerPOCell: {
        css: css`
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
            }
        `,
    },
    dataCell: {
        css: css`
            & > ${StyledButton}, & > ${StyledButton} > ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 24px;
                font-weight: normal;
                color: ${color("text.main")};
                &:hover {
                    color: ${color("common.ui1")};
                }
            }
        `,
    },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    isReordering,
    reorder,
    showAddToCartConfirmationDialog,
    order,
    toaster,
    orderSettings,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const onClickReorder = () => {
        reorder({
            orderNumber,
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toaster.addToast({
                    body: (
                        <>
                            <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />
                            <StyledWrapper as="span">&nbsp;{translate("Added to Cart")}</StyledWrapper>
                        </>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };
    // <XNG-Changes>
    const companyName = xIsPickup(order.fulfillmentMethod)
        ? order.properties.shippingAddressInfo
            ? JSON.parse(order.properties.shippingAddressInfo).companyName
            : ""
        : order.stCompanyName;
    // </XNG-Changes>

    // XNG-Changes: use Notify and change structure to Table
    return (
        <>
            <DataTableCell {...styles.dataCell}>
                {orderDateDisplay && (
                    <>
                        {orderNumber && <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={orderNumber} />}
                        {!orderNumber && <>{orderDateDisplay}</>}
                    </>
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell {...styles.companyNameCell}>{`${companyName || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${order.statusDisplay || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell {...styles.customerPOCell}>{`${order.customerPO || EMPTY_VALUE}`}</DataTableCell>
            {/* XNG-Change: display orderSubTotalDisplay */}
            <DataTableCell {...styles.dataCell}>{`${order.orderSubTotalDisplay || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {isReordering[orderNumber] ? (
                    <Button {...styles.reorderButton} disabled={true}>
                        <LoadingSpinner size={12} />
                    </Button>
                ) : (
                    <Button
                        disabled={Object.keys(isReordering).length > 0}
                        onClick={onClickReorder}
                        {...styles.reorderButton}
                    >
                        {translate("Reorder")}
                    </Button>
                )}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(OrderSummaryCard));
